import { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { AlertTriangle, Loader, FlaskConical, Zap } from 'lucide-react'
import DashboardSidebar from './DashboardSidebar'
import MetricCards from './MetricCards'
import ReviewTable from './ReviewTable'
import ReviewDetailPanel from './ReviewDetailPanel'
import InboxView from './InboxView'
import SettingsPage from './SettingsPage'
import AnalyticsPage from './AnalyticsPage'
import AddLocationModal from './AddLocationModal'
import BulkReviewQueuePanel from './BulkReviewQueuePanel'
import { getReviews, getStats, approveReview, rejectReview, fetchLiveReview, bulkGenerateReviews, processLocationReviews } from '../../services/dashboardApi'
import type { CustomerConfig, DashboardReview, ReviewStats, Platform, ActivePage } from '../../types/dashboard'
import { computeDataSourceState } from '../../types/dashboard'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

// ── Types ─────────────────────────────────────────────────────────────────────

interface PlatformConnectionSummary {
  platform: string
  shop_domain: string | null
}

interface DashboardLocation {
  id: string
  name: string
  address: string | null
  has_google_credentials: boolean
  notification_email: string | null
  fetch_frequency: string | null
  brand_voice: string | null
  auto_respond_enabled: boolean
  platform_connections: PlatformConnectionSummary[]
  brief_enabled: boolean
  brief_day_of_week: number
  brief_hour: number
  brief_timezone: string
}

interface DashboardData {
  customer_id: string
  full_name: string
  business_name: string
  email: string
  plan: string
  api_key: string
  max_locations: number
  config: CustomerConfig
  locations: DashboardLocation[]
}

interface Toast {
  message: string
  type: 'success' | 'danger' | 'info'
}

// ── Toast ─────────────────────────────────────────────────────────────────────

function ToastBanner({ toast }: { toast: Toast | null }) {
  if (!toast) return null
  const colors: Record<Toast['type'], string> = {
    success: 'border-rd-accent/30 text-rd-accent',
    danger: 'border-danger-500/30 text-danger-500',
    info: 'border-primary-500/30 text-primary-500',
  }
  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] bg-rd-surface2 border rounded-xl px-5 py-2.5 text-[13px] whitespace-nowrap shadow-lg ${colors[toast.type]}`}
    >
      {toast.message}
    </div>
  )
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate()

  const [data, setData] = useState<DashboardData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [reviews, setReviews] = useState<DashboardReview[]>([])
  const [stats, setStats] = useState<ReviewStats | null>(null)
  const [isLoadingReviews, setIsLoadingReviews] = useState(false)

  const [selectedLocationId, setSelectedLocationId] = useState('')
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>('all')
  const [selectedRating, setSelectedRating] = useState<number | 'all'>('all')
  const [activePage, setActivePage] = useState<ActivePage>('dashboard')
  const [activeReviewId, setActiveReviewId] = useState<string | null>(null)
  const [isFetchingLive, setIsFetchingLive] = useState(false)
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null)
  const [toast, setToast] = useState<Toast | null>(null)
  const [showAddLocation, setShowAddLocation] = useState(false)
  const [selectedReviewIds, setSelectedReviewIds] = useState<Set<string>>(new Set())
  const [isBulkGenerating, setIsBulkGenerating] = useState(false)
  const [bulkQueueIds, setBulkQueueIds] = useState<string[] | null>(null)

  const jwt = localStorage.getItem('jwt_token') ?? ''

  const showToast = useCallback((t: Toast) => {
    setToast(t)
    setTimeout(() => setToast(null), t.type === 'danger' ? 6000 : 3000)
  }, [])

  // ── Fetch customer + locations ─────────────────────────────────────────────

  const fetchDashboard = useCallback(async () => {
    if (!jwt) { navigate('/login'); return }
    setIsLoading(true)
    setLoadError(null)
    try {
      const res = await fetch(`${BASE_URL}/api/dashboard`, {
        headers: { Authorization: `Bearer ${jwt}` },
      })
      if (res.status === 401) { localStorage.removeItem('jwt_token'); navigate('/login'); return }
      if (!res.ok) throw new Error(`Failed to load dashboard (${res.status})`)
      const json: DashboardData = await res.json()
      setData(json)
      if (json.locations.length > 0 && !selectedLocationId) {
        setSelectedLocationId(json.locations[0].id)
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load dashboard')
    } finally {
      setIsLoading(false)
    }
  }, [jwt, navigate, selectedLocationId])

  useEffect(() => { fetchDashboard() }, [])

  // ── Fetch reviews + stats when location changes ────────────────────────────

  const fetchReviewsAndStats = useCallback(async (apiKey: string, locationId: string) => {
    if (!apiKey || !locationId) return
    setIsLoadingReviews(true)
    try {
      const [fetchedReviews, fetchedStats] = await Promise.all([
        getReviews(apiKey, locationId),
        getStats(apiKey, locationId),
      ])
      setReviews(fetchedReviews)
      setStats(fetchedStats)
      setLastSyncedAt(new Date())
    } catch (err) {
      if (err instanceof Error && err.message === 'unauthorized') {
        localStorage.removeItem('jwt_token')
        navigate('/login')
      } else {
        showToast({ message: err instanceof Error ? err.message : 'Failed to load reviews', type: 'danger' })
      }
    } finally {
      setIsLoadingReviews(false)
    }
  }, [navigate])

  useEffect(() => {
    if (data?.api_key && selectedLocationId) {
      fetchReviewsAndStats(data.api_key, selectedLocationId)
    }
  }, [data?.api_key, selectedLocationId])

  // ── Available platforms (drives the dynamic dropdown) ────────────────────

  const availablePlatforms = useMemo<Platform[]>(() => {
    const platforms = new Set<Platform>(['google'])
    reviews.forEach((r) => {
      if (r.platform && r.platform !== 'google') platforms.add(r.platform as Platform)
    })
    return Array.from(platforms)
  }, [reviews])

  // ── Rating filter (drives the dynamic dropdown, same pattern as platforms) ──

  const availableRatings = useMemo<number[]>(() => {
    const ratings = new Set<number>()
    reviews.forEach((r) => {
      if (r.rating) ratings.add(r.rating)
    })
    return Array.from(ratings).sort((a, b) => b - a)
  }, [reviews])

  // ── Platform + rating filters (client-side) ────────────────────────────────

  const applyFilters = (list: DashboardReview[]) =>
    list.filter((r) => {
      if (selectedPlatform !== 'all' && (r.platform ?? 'google') !== selectedPlatform) return false
      if (selectedRating !== 'all' && r.rating !== selectedRating) return false
      return true
    })

  const filteredReviews = applyFilters(reviews)

  // ── Fetch live review via SerpAPI ─────────────────────────────────────────

  const handleFetchLiveReview = async () => {
    if (!selectedLocationId || !data) return
    setIsFetchingLive(true)
    try {
      await fetchLiveReview(jwt, selectedLocationId)
      showToast({ message: 'Live review fetched and processed by AI!', type: 'success' })
      await fetchReviewsAndStats(data.api_key, selectedLocationId)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fetch failed'
      if (msg.includes('All available reviews')) {
        showToast({ message: 'No new reviews found — check back later.', type: 'info' })
      } else if (msg.includes('not configured')) {
        showToast({ message: 'Live review fetching is not set up yet.', type: 'info' })
      } else if (msg.includes('not find your business')) {
        showToast({ message: 'Could not find your business on Google Maps. Update location name/address in Settings.', type: 'danger' })
      } else {
        showToast({ message: msg, type: 'danger' })
      }
    } finally {
      setIsFetchingLive(false)
    }
  }

  // ── Approve / reject ──────────────────────────────────────────────────────

  const handleApprove = async (reviewId: string, responseText: string) => {
    await approveReview(jwt, reviewId, responseText)
    if (data?.api_key && selectedLocationId) {
      await fetchReviewsAndStats(data.api_key, selectedLocationId)
    }
  }

  const handleReject = async (reviewId: string) => {
    await rejectReview(jwt, reviewId)
    if (data?.api_key && selectedLocationId) {
      await fetchReviewsAndStats(data.api_key, selectedLocationId)
    }
  }

  // ── Bulk select + generate ────────────────────────────────────────────────

  const toggleSelectReview = (id: string) => {
    setSelectedReviewIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAllReviews = (visibleReviews: DashboardReview[]) => {
    // An already-posted review can't be regenerated — exclude it so "select
    // all" never selects something bulk-generate will silently drop.
    const selectable = visibleReviews.filter((r) => r.status !== 'responded')
    setSelectedReviewIds((prev) => {
      const allSelected = selectable.length > 0 && selectable.every((r) => prev.has(r.id))
      return allSelected ? new Set() : new Set(selectable.map((r) => r.id))
    })
  }

  const clearSelection = () => setSelectedReviewIds(new Set())

  const handleBulkGenerate = async () => {
    if (selectedReviewIds.size === 0 || !data) return
    setIsBulkGenerating(true)
    try {
      const idsToGenerate = [...selectedReviewIds]
      const { results, errors } = await bulkGenerateReviews(jwt, idsToGenerate)
      if (data.api_key && selectedLocationId) {
        await fetchReviewsAndStats(data.api_key, selectedLocationId)
      }
      if (errors.length > 0) {
        showToast({
          message: `${errors.length} of ${idsToGenerate.length} review(s) could not be generated.`,
          type: 'danger',
        })
      }
      const actionableIds = results
        .filter((r) => r.success && (r.status === 'pending_approval' || r.status === 'escalated'))
        .map((r) => r.review_id)
      setSelectedReviewIds(new Set())
      if (actionableIds.length > 0) {
        setBulkQueueIds(actionableIds)
      } else if (errors.length === 0) {
        showToast({ message: 'No reviews needed a response.', type: 'info' })
      }
    } catch (err) {
      showToast({ message: err instanceof Error ? err.message : 'Failed to generate responses', type: 'danger' })
    } finally {
      setIsBulkGenerating(false)
    }
  }

  // ── Add location ──────────────────────────────────────────────────────────

  const handleLocationAdded = async (locationId: string) => {
    setShowAddLocation(false)
    await fetchDashboard()
    setSelectedLocationId(locationId)
    setActivePage('settings')
    showToast({ message: 'Location added — connect its Google Business Profile below.', type: 'success' })
  }

  // ── Active review ─────────────────────────────────────────────────────────

  const activeReview = activeReviewId
    ? (reviews.find((r) => r.id === activeReviewId) ?? null)
    : null

  // ── Shared nav ────────────────────────────────────────────────────────────

  const nav = (
    <nav className="h-[52px] flex items-center justify-between px-5 bg-rd-surface border-b border-white/[0.08] flex-shrink-0 z-10">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-2 no-underline hover:opacity-80 transition-opacity"
      >
        <img
          src={`${import.meta.env.BASE_URL}logo-icon.png`}
          alt="ReputeDesk"
          className="w-7 h-7 object-contain"
        />
        <span className="font-sora font-bold text-[15px] text-rd-text">ReputeDesk</span>
      </Link>
    </nav>
  )

  // ── Loading / error states ────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="h-screen bg-rd-bg font-dmSans flex flex-col">
        {nav}
        <div className="flex items-center justify-center flex-1" role="status" aria-live="polite">
          <Loader className="w-5 h-5 animate-spin text-rd-accent" />
          <span className="ml-3 text-rd-muted text-sm">Loading your dashboard…</span>
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="h-screen bg-rd-bg font-dmSans flex flex-col">
        {nav}
        <div className="flex flex-col items-center justify-center flex-1 gap-4" role="alert">
          <AlertTriangle className="w-10 h-10 text-warning-500" />
          <p className="text-rd-muted text-sm">{loadError}</p>
          <button
            type="button"
            onClick={fetchDashboard}
            className="px-4 py-2 rounded-lg text-sm font-medium text-rd-bg"
            style={{ background: 'linear-gradient(135deg, #4ade80, #22d3ee)' }}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!data) return null

  const escalatedCount = stats?.total_escalated ?? 0
  const inboxCount = reviews.filter((r) => r.status === 'pending_approval' || r.status === 'escalated').length
  const dataSourceState = computeDataSourceState(filteredReviews)
  const selectedLocation = data.locations.find((l) => l.id === selectedLocationId)
  const selectedLocationHasGoogle = selectedLocation?.has_google_credentials ?? false

  // ── Main render ───────────────────────────────────────────────────────────

  const emailVerified = localStorage.getItem('email_verified') !== 'false'

  return (
    <div className="h-screen bg-rd-bg font-dmSans flex flex-col overflow-hidden">
      {nav}

      {!emailVerified && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/20 px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
          <span className="text-yellow-300">
            ⚠ Please verify your email address to ensure uninterrupted access.
          </span>
          <button
            onClick={async () => {
              const { resendVerification } = await import('../../services/registrationApi')
              const email = localStorage.getItem('customer_email') ?? ''
              if (email) await resendVerification(email)
            }}
            className="text-yellow-300 underline text-xs font-medium whitespace-nowrap"
          >
            Resend link
          </button>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <DashboardSidebar
          businessName={data.business_name}
          plan={data.plan}
          maxLocations={data.max_locations}
          locations={data.locations.map((l) => ({ id: l.id, name: l.name }))}
          selectedLocationId={selectedLocationId}
          onLocationChange={setSelectedLocationId}
          onAddLocation={() => setShowAddLocation(true)}
          activePage={activePage}
          onPageChange={setActivePage}
          escalatedCount={escalatedCount}
          inboxCount={inboxCount}
          onLogout={() => {
            localStorage.removeItem('jwt_token')
            localStorage.removeItem('email_verified')
            localStorage.removeItem('customer_email')
            localStorage.removeItem('onboarding_completed')
            navigate('/login')
          }}
        />

        {/* Main content */}
        <main className="flex-1 overflow-hidden flex flex-col">

          {/* Dashboard view */}
          {activePage === 'dashboard' && (
            <div className="flex-1 overflow-hidden flex flex-col gap-3 p-4">
              <MetricCards stats={stats} isLoading={isLoadingReviews} />

              {/* Data-source banner */}
              {!isLoadingReviews && dataSourceState === 'all_demo' && (
                <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-warning-500/20 bg-warning-500/5 flex-shrink-0">
                  <FlaskConical className="w-4 h-4 text-warning-500 shrink-0" />
                  <p className="text-[12px] text-rd-muted flex-1">
                    <span className="font-medium text-warning-500">Demo data</span>
                    {' '}— these are sample reviews so you can explore the dashboard.
                    Click <span className="font-medium text-rd-text">Fetch live review</span> to import a real Google review for your business.
                  </p>
                </div>
              )}
              {!isLoadingReviews && dataSourceState === 'has_serpapi' && (
                <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-primary-500/20 bg-primary-500/5 flex-shrink-0">
                  <Zap className="w-4 h-4 text-primary-500 shrink-0" />
                  <p className="text-[12px] text-rd-muted flex-1">
                    <span className="font-medium text-primary-500">Live read-only data</span>
                    {' '}— reviews are fetched from Google Maps. AI responses are generated but not auto-posted.
                    Connect your Google Business Profile in <span className="font-medium text-rd-text">Settings</span> to enable posting.
                  </p>
                </div>
              )}

              <ReviewTable
                reviews={filteredReviews}
                isLoading={isLoadingReviews}
                isFetchingLive={isFetchingLive}
                lastSyncedAt={lastSyncedAt}
                availablePlatforms={availablePlatforms}
                selectedPlatform={selectedPlatform}
                onPlatformChange={setSelectedPlatform}
                onRowClick={setActiveReviewId}
                onFetchLive={selectedLocationHasGoogle ? handleFetchLiveReview : undefined}
                selectedIds={selectedReviewIds}
                onToggleSelect={toggleSelectReview}
                onToggleSelectAll={() => toggleSelectAllReviews(filteredReviews)}
                onClearSelection={clearSelection}
                availableRatings={availableRatings}
                selectedRating={selectedRating}
                onRatingChange={setSelectedRating}
                onBulkGenerate={handleBulkGenerate}
                isBulkGenerating={isBulkGenerating}
              />
            </div>
          )}

          {/* Escalations view */}
          {activePage === 'escalations' && (
            <div className="flex-1 overflow-hidden flex flex-col gap-3 p-4">
              <ReviewTable
                reviews={applyFilters(reviews.filter((r) => r.status === 'escalated' || r.escalated_to_human))}
                isLoading={isLoadingReviews}
                isFetchingLive={false}
                lastSyncedAt={lastSyncedAt}
                availablePlatforms={availablePlatforms}
                selectedPlatform={selectedPlatform}
                onPlatformChange={setSelectedPlatform}
                onRowClick={setActiveReviewId}
                selectedIds={selectedReviewIds}
                onToggleSelect={toggleSelectReview}
                onToggleSelectAll={() => toggleSelectAllReviews(applyFilters(reviews.filter((r) => r.status === 'escalated' || r.escalated_to_human)))}
                onClearSelection={clearSelection}
                availableRatings={availableRatings}
                selectedRating={selectedRating}
                onRatingChange={setSelectedRating}
                onBulkGenerate={handleBulkGenerate}
                isBulkGenerating={isBulkGenerating}
              />
            </div>
          )}

          {/* Inbox view */}
          {activePage === 'inbox' && (
            <InboxView
              reviews={reviews}
              isLoading={isLoadingReviews}
              onApprove={handleApprove}
              onReject={handleReject}
              onToast={showToast}
            />
          )}

          {/* Settings view */}
          {activePage === 'settings' && (
            <SettingsPage
              data={data}
              selectedLocationId={selectedLocationId}
              onSettingsSaved={fetchDashboard}
              onToast={showToast}
              onGetReviews={(locId) => processLocationReviews(data.api_key, locId)}
              onCredentialsSaved={fetchDashboard}
              onJudgeMeConnected={fetchDashboard}
              onJudgeMeDisconnected={fetchDashboard}
            />
          )}

          {/* Analytics view */}
          {activePage === 'analytics' && (
            <AnalyticsPage apiKey={data.api_key} locationId={selectedLocationId} />
          )}
        </main>
      </div>

      {/* Detail panel */}
      <ReviewDetailPanel
        review={activeReview}
        onClose={() => setActiveReviewId(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        onToast={showToast}
      />

      {/* Bulk review queue — opened after "Generate responses for N" */}
      {bulkQueueIds && (
        <BulkReviewQueuePanel
          reviewIds={bulkQueueIds}
          reviews={reviews}
          onApprove={handleApprove}
          onReject={handleReject}
          onClose={() => setBulkQueueIds(null)}
          onToast={showToast}
        />
      )}

      {/* Toast */}
      <ToastBanner toast={toast} />

      {/* Add location modal */}
      {showAddLocation && (
        <AddLocationModal
          apiKey={data.api_key}
          onSuccess={handleLocationAdded}
          onClose={() => setShowAddLocation(false)}
        />
      )}
    </div>
  )
}
